'use client'

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAudioPlayer } from '../AudioProvider'
import { ForwardButton } from './ForwardButton'
import { MuteButton } from './MuteButton'
import { PlaybackRateButton } from './PlaybackRateButton'
import { PlayButton } from './PlayButton'
import { RewindButton } from './RewindButton'
import { Slider } from './Slider'

function parseTime(seconds) {
  let hours = Math.floor(seconds / 3600)
  let minutes = Math.floor((seconds - hours * 3600) / 60)
  seconds = seconds - hours * 3600 - minutes * 60
  return [hours, minutes, seconds]
}

function formatHumanTime(seconds) {
  let [h, m, s] = parseTime(seconds)
  return `${h} hour${h === 1 ? '' : 's'}, ${m} minute${
    m === 1 ? '' : 's'
  }, ${s} second${s === 1 ? '' : 's'}`
}

export function AudioPlayer() {
  const player = useAudioPlayer()
  const audioRef = useRef(null)
  const wasPlayingRef = useRef(false)
  const [currentTime, setCurrentTime] = useState(player.currentTime)

  useEffect(() => {
    setCurrentTime(null)
  }, [player.currentTime])

  useEffect(() => {
    if (player.episode) {
      console.log('Setting media session metadata for:', player.episode.title)
      navigator.mediaSession.metadata = new MediaMetadata({
        title: player.episode.title,
        artist: player.episode.description,
        album: 'Student Music',
        artwork: [
          {
            src: '/wp-content/themes/magazine/2025cover.png',
            sizes: '96x96',
            type: 'image/png',
          },
          {
            src: '/wp-content/themes/magazine/2025cover.png',
            sizes: '128x128',
            type: 'image/png',
          },
          {
            src: '/wp-content/themes/magazine/2025cover.png',
            sizes: '192x192',
            type: 'image/png',
          },
          {
            src: '/wp-content/themes/magazine/2025cover.png',
            sizes: '256x256',
            type: 'image/png',
          },
          {
            src: '/wp-content/themes/magazine/2025cover.png',
            sizes: '384x384',
            type: 'image/png',
          },
          {
            src: '/wp-content/themes/magazine/2025cover.png',
            sizes: '512x512',
            type: 'image/png',
          },
        ],
      })

      // Set media session action handlers
      navigator.mediaSession.setActionHandler('play', () => player.play())
      navigator.mediaSession.setActionHandler('pause', () => player.pause())
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        player.seek(player.currentTime - (details.seekOffset || 10))
      })
      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        player.seek(player.currentTime + (details.seekOffset || 10))
      })
      navigator.mediaSession.setActionHandler(
        'previoustrack',
        player.playPrevious
      )
      navigator.mediaSession.setActionHandler('nexttrack', player.playNext)
      navigator.mediaSession.setActionHandler('stop', () => {
        player.pause()
        player.seek(0)
      })
    }
  }, [player.episode])

  if (!player.episode) {
    return null
  }

  return (
    <div className="flex items-center gap-6 bg-white/90 px-4 py-4 pb-12 shadow shadow-neutral-200/80 ring-1 ring-neutral-900/5 backdrop-blur-sm md:px-6">
      <div className="hidden md:block">
        <PlayButton player={player} />
      </div>
      <div className="mb-[env(safe-area-inset-bottom)] flex flex-1 flex-col gap-3 overflow-hidden p-1">
        <div className="truncate text-center text-sm font-bold leading-6 md:text-left">
          {player.episode.title}
        </div>
        <div className="flex justify-between gap-6">
          <div className="flex items-center md:hidden">
            <MuteButton player={player} />
          </div>
          <div className="flex flex-none items-center gap-4">
            <RewindButton player={player} />
            <div className="md:hidden">
              <PlayButton player={player} />
            </div>
            <ForwardButton player={player} />
          </div>
          <Slider
            label="Current time"
            maxValue={player.duration}
            step={1}
            value={[currentTime ?? player.currentTime]}
            onChange={([value]) => setCurrentTime(value)}
            onChangeEnd={([value]) => {
              player.seek(value)
              if (wasPlayingRef.current) {
                player.play()
              }
            }}
            numberFormatter={{ format: formatHumanTime }}
            onChangeStart={() => {
              wasPlayingRef.current = player.playing
              player.pause()
            }}
          />
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <PlaybackRateButton player={player} />
            </div>
            <div className="hidden items-center md:flex">
              <MuteButton player={player} />
            </div>
          </div>
        </div>
      </div>
      <audio ref={audioRef} />
    </div>
  )
}
